'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DataTable.Body
 *
 * Renders <tbody> with one <tr> per visible row. Supports:
 *   - Row selection (checkbox per row, Shift+Click range selection).
 *   - Optional onRowClick handler.
 *   - Selection-change debounced live-region (250 ms trailing edge).
 */
import { useRef, useState, useEffect } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useDataTable } from './context';
import styles from './DataTable.module.css';
export function Body({ onRowClick, verticalAlign, emptyMessage }) {
    const { table, totalRows, selectionEnabled, virtual, stickyHeaderVar } = useDataTable();
    const [selAnnounce, setSelAnnounce] = useState('');
    const debounceRef = useRef(null);
    const lastClickedRowRef = useRef(null);
    const rows = table.getRowModel().rows;
    const visibleColumnCount = table.getVisibleLeafColumns().length + (selectionEnabled ? 1 : 0);
    const selectedCount = Object.keys(table.getState().rowSelection).length;
    // Debounced selection announcement (250 ms trailing)
    useEffect(() => {
        if (!selectionEnabled)
            return;
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSelAnnounce(`${selectedCount} of ${totalRows} rows selected`);
        }, 250);
        return () => {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [selectedCount, totalRows, selectionEnabled]);
    function handleRowClick(e, rowIndex, row) {
        if (selectionEnabled) {
            if (e.shiftKey && lastClickedRowRef.current !== null) {
                // Range select
                const start = Math.min(lastClickedRowRef.current, rowIndex);
                const end = Math.max(lastClickedRowRef.current, rowIndex);
                for (let i = start; i <= end; i++) {
                    rows[i]?.toggleSelected(true);
                }
            }
            else {
                row.toggleSelected();
            }
            lastClickedRowRef.current = rowIndex;
        }
        onRowClick?.(row.original, e);
    }
    return (_jsxs("tbody", { className: styles.tbody, role: "rowgroup", children: [rows.length === 0 && emptyMessage !== undefined ? (_jsx("tr", { className: styles.tr, children: _jsx("td", { className: styles.td, colSpan: visibleColumnCount, style: { textAlign: 'center', color: 'var(--muted-foreground)' }, children: emptyMessage }) })) : null, selectionEnabled && (_jsx("tr", { "aria-hidden": "true", style: { display: 'none' }, children: _jsx("td", { children: _jsx("span", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: styles.srOnly, children: selAnnounce }) }) })), rows.map((row, rowIndex) => (_jsxs("tr", { role: "row", className: [
                    styles.tr,
                    row.getIsSelected() ? styles.trSelected : '',
                    onRowClick || selectionEnabled ? styles.trClickable : '',
                ]
                    .filter(Boolean)
                    .join(' '), onClick: (e) => handleRowClick(e, rowIndex, row), "aria-selected": selectionEnabled ? row.getIsSelected() : undefined, children: [selectionEnabled && (_jsx("td", { className: styles.tdCheckbox, children: _jsx("input", { type: "checkbox", className: styles.checkbox, checked: row.getIsSelected(), onChange: () => {
                                row.toggleSelected();
                                lastClickedRowRef.current = rowIndex;
                            }, "aria-label": `Select row ${rowIndex + 1}`, style: { scrollMarginBlockStart: `var(${stickyHeaderVar}, 0)` } }) })), row.getVisibleCells().map((cell) => {
                        const { meta, header } = cell.column.columnDef;
                        const width = typeof meta?.width === 'number' ? `${meta.width}px` : meta?.width;
                        // The header text doubles as the mobile inline label (rendered via
                        // CSS `::before { content: attr(data-label) }`). Only string
                        // headers carry a label; nodes (sort buttons) collapse to none.
                        const label = typeof header === 'string' ? header : '';
                        return (_jsx("td", { role: virtual ? 'gridcell' : 'cell', className: styles.td, "data-label": label, "data-primary": meta?.mobilePrimary ? '' : undefined, "data-mobile-hide": meta?.mobileHide ? '' : undefined, style: {
                                scrollMarginBlockStart: `var(${stickyHeaderVar}, 0)`,
                                '--dt-col-width': width,
                                '--dt-col-align': meta?.align,
                                '--dt-col-white-space': meta?.nowrap ? 'nowrap' : undefined,
                                verticalAlign,
                            }, children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id));
                    })] }, row.id)))] }));
}
//# sourceMappingURL=Body.js.map